import React, { cloneElement, useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router'
import { useEffectOnce } from '@chilecompra/react-kit'
import { useViewport } from '@chilecompra/react-kit/components'
import routes from '../../config/settings/routes'
import { useSecureURL } from '../../modules/hooks/security'
import {
  Root,
  StepInfo,
  StepIcon,
  DesktopSteps,
  DesktopStepsContent,
  DesktopStepsLine,
  DesktopStep,
  DesktopStepInfoTitle,
  DesktopStepInfoSubtitle,
  MobileStep,
  MobileStepInfoTitle,
  MobileStepInfoSubtitle,
  Content,
  Header,
  Brand,
  BrandLogo,
  ContentMiddle
} from './WizardLayout.styles'

/**
 * The WizardLayout's component.
 */
const WizardLayout = props => {
  const { defaultStepID, steps } = props
  const navigate = useNavigate()
  const { size } = useViewport()
  const getSecureRoute = useSecureURL()
  const [currentStepID, setCurrentStepID] = useState(defaultStepID)
  const currentStep = useMemo(() => steps.find(step => step.id === currentStepID) || steps[0], [currentStepID, steps])

  const stepsByGroup = useMemo(
    () =>
      steps.reduce((result, step) => {
        const found = result.findIndex(resultStep => resultStep.number === step.number)

        if (found !== -1 && result[found].number === currentStep.number && result[found].name !== currentStep.name) {
          result[found] = step
        }

        if (step.hiddenWhen.includes(currentStep.id) || found !== -1 || step.name === undefined) {
          return result
        }

        return result.concat(step)
      }, []),
    [steps, currentStep]
  )

  const getStepStatus = useCallback(
    number => {
      if (number === currentStep.number) {
        return 'active'
      }

      if (number < currentStep.number) {
        return 'completed'
      }

      return undefined
    },
    [currentStep.number]
  )

  const handleStepChange = stepID => setCurrentStepID(stepID)
  const handleDefaultStep = () => {
    navigate(getSecureRoute(routes.registerWizard), { replace: true })
    setCurrentStepID(defaultStepID)
  }

  useEffectOnce(() => {
    window.history.replaceState({}, null)
  })

  const renderHeader = () => {
    return (
      <Header>
        <Brand onClick={handleDefaultStep}>
          <BrandLogo alt="Mercado Público" src="/assets/img/mercadopublico-logo.svg" />
        </Brand>
      </Header>
    )
  }

  return (
    <Root>
      {((!size.isTiny && currentStep.id === 'welcome') || size.isDownToLarge) && renderHeader()}
      <Content>
        {!size.isTiny && (size.isUpToLarge || currentStep.id !== 'by-unique-pass') && currentStep.name && (
          <DesktopSteps>
            <DesktopStepsContent>
              <DesktopStepsLine />
              {stepsByGroup.map(step => {
                const status = getStepStatus(step.number)
                return (
                  <DesktopStep key={step.id}>
                    <StepInfo>
                      {status && step.showNumber && (
                        <DesktopStepInfoTitle>
                          {step.showNumber ? `Paso ${step.number}` : step.name}
                        </DesktopStepInfoTitle>
                      )}
                      {!status && step.showNumber && (
                        <DesktopStepInfoTitle>
                          {step.showNumber ? `Paso ${step.number}` : step.name}
                        </DesktopStepInfoTitle>
                      )}
                      <DesktopStepInfoSubtitle>{step.name}</DesktopStepInfoSubtitle>
                    </StepInfo>
                    <StepIcon status={status}>{step.icon}</StepIcon>
                  </DesktopStep>
                )
              })}
            </DesktopStepsContent>
          </DesktopSteps>
        )}
        <ContentMiddle>
          {!size.isTiny && currentStep.id !== 'welcome' && !size.isDownToLarge && renderHeader()}
          {size.isTiny && currentStep.name && (
            <MobileStep>
              <StepIcon status="active">{currentStep.icon}</StepIcon>
              <StepInfo>
                <MobileStepInfoTitle>
                  {currentStep.showNumber
                    ? `Paso ${currentStep.number} de ${stepsByGroup.filter(({ showNumber }) => showNumber).length}`
                    : currentStep.name}
                </MobileStepInfoTitle>
                {currentStep.showNumber && <MobileStepInfoSubtitle>{currentStep.name}</MobileStepInfoSubtitle>}
              </StepInfo>
            </MobileStep>
          )}
          {cloneElement(currentStep.component, { onChangeStep: handleStepChange })}
        </ContentMiddle>
      </Content>
    </Root>
  )
}

WizardLayout.propTypes = {
  defaultStepID: PropTypes.string.isRequired,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      component: PropTypes.node.isRequired,
      icon: PropTypes.node,
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      number: PropTypes.number.isRequired,
      showNumber: PropTypes.bool.isRequired,
      hiddenWhen: PropTypes.arrayOf(PropTypes.string).isRequired
    })
  ).isRequired
}

export default WizardLayout
